import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import {
  CatalogOffering, CatalogService, InventoryLot, InventoryRecallAuditEvent, InventoryRecallCampaign, InventoryRecallImpactLine, InventoryRecallLine
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-recall-detail',
  imports: [
    AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    SelectModule, TextareaModule, TableModule
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('catalog.recall.campaigns.createTitle') : t('catalog.recall.campaigns.detailTitle')" [description]="t('catalog.recall.campaigns.detailDescription')">
      <ain-inventory-page-hint pageKey="recallDetail" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !campaign()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error() || t('catalog.recall.campaigns.notFound')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.recall.campaigns.newRecord') : campaign()!.campaignNumber }}</h3>
            <p>{{ isCreateMode() ? t('catalog.recall.campaigns.createHint') : campaign()!.status }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/inventory/recalls']">{{ t('catalog.action.backToList') }}</a>
            @if (!isCreateMode() && canSimulate()) {
              <button pButton type="button" [label]="t('catalog.recall.action.simulate')" severity="secondary" [disabled]="simulating()" (click)="simulate()"></button>
            }
            @if (!isCreateMode() && canSubmitForApproval()) {
              <button pButton type="button" [label]="t('catalog.recall.action.submitForApproval')" severity="info" [disabled]="submitting()" (click)="submitForApproval()"></button>
            }
            @if (!isCreateMode() && canApprove()) {
              <button pButton type="button" [label]="t('catalog.recall.action.approve')" severity="success" [disabled]="approving()" (click)="approve()"></button>
            }
            @if (!isCreateMode() && canExecute()) {
              <button pButton type="button" [label]="executeLabel()" severity="warn" [disabled]="executing()" (click)="execute()"></button>
            }
            @if (!isCreateMode() && canEnqueueExecution()) {
              <button pButton type="button" [label]="t('catalog.recall.action.enqueueExecution')" severity="secondary" [disabled]="enqueuing()" (click)="enqueueExecution()"></button>
            }
            @if (!isCreateMode() && canCancel()) {
              <button pButton type="button" [label]="t('catalog.recall.action.cancel')" severity="danger" [disabled]="cancelling()" (click)="cancel()"></button>
            }
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="saving()" (click)="save()"></button>
            }
          </div>
        </section>

        <section class="form-grid">
          <label>
            <span>{{ t('catalog.recall.campaignNumber') }}</span>
            <input pInputText type="text" [(ngModel)]="form.campaignNumber" [disabled]="!isCreateMode()" />
          </label>
          <label>
            <span>{{ t('catalog.recall.name') }}</span>
            <input pInputText type="text" [(ngModel)]="form.name" [disabled]="!isCreateMode()" />
          </label>
          <label>
            <span>{{ t('catalog.recall.reasonCode') }}</span>
            <input pInputText type="text" [(ngModel)]="form.reasonCode" [disabled]="!isCreateMode()" />
          </label>
          <label class="full">
            <span>{{ t('catalog.recall.description') }}</span>
            <textarea pTextarea rows="3" [(ngModel)]="form.description" [disabled]="!isCreateMode()"></textarea>
          </label>
          <label>
            <span>{{ t('catalog.inventory.offeringId') }}</span>
            <p-select [options]="offeringOptions()" [ngModel]="form.offeringId" (ngModelChange)="onOfferingChange($event)" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
          </label>
          <label>
            <span>{{ t('catalog.tracking.lotId') }}</span>
            <p-select [options]="lotOptions()" [(ngModel)]="form.lotId" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
          </label>
        </section>

        @if (!isCreateMode()) {
          <section class="summary-grid">
            <article><strong>{{ t('catalog.recall.totalLines') }}</strong><span>{{ campaign()!.totalLines }}</span></article>
            <article><strong>{{ t('catalog.recall.processedLines') }}</strong><span>{{ campaign()!.processedLines }}</span></article>
            <article><strong>{{ t('catalog.recall.quarantinedLines') }}</strong><span>{{ campaign()!.quarantinedLines }}</span></article>
            <article><strong>{{ t('catalog.recall.skippedLines') }}</strong><span>{{ campaign()!.skippedLines }}</span></article>
            <article><strong>{{ t('catalog.recall.customerImpactCount') }}</strong><span>{{ campaign()!.customerImpactCount }}</span></article>
            <article><strong>{{ t('catalog.recall.warehouseImpactCount') }}</strong><span>{{ campaign()!.warehouseImpactCount }}</span></article>
          </section>

          <nav class="tab-nav">
            <button type="button" [class.active]="activeTab() === 'targets'" (click)="activeTab.set('targets')">{{ t('catalog.recall.lines.title') }}</button>
            <button type="button" [class.active]="activeTab() === 'impact'" (click)="activeTab.set('impact')">{{ t('catalog.recall.impact.title') }}</button>
            <button type="button" [class.active]="activeTab() === 'audit'" (click)="openAuditTab()">{{ t('catalog.recall.audit.title') }}</button>
          </nav>

          @if (activeTab() === 'targets') {
            <section class="lines-panel">
              <p-table [value]="lines()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.recall.lines.targetType') }}</th>
                    <th>{{ t('catalog.recall.lines.targetNumber') }}</th>
                    <th>{{ t('catalog.tracking.status') }}</th>
                    <th>{{ t('catalog.recall.lines.resultNotes') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.targetType }}</td>
                    <td>{{ row.targetNumber }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.resultNotes || '-' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          } @else if (activeTab() === 'impact') {
            <section class="lines-panel">
              @if (!campaign()!.simulatedAtUtc) {
                <p class="hint">{{ t('catalog.recall.impact.simulateHint') }}</p>
              } @else {
                <p-table [value]="impactLines()">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('catalog.recall.impact.type') }}</th>
                      <th>{{ t('catalog.recall.lines.targetNumber') }}</th>
                      <th>{{ t('catalog.recall.impact.referenceNumber') }}</th>
                      <th>{{ t('catalog.recall.impact.quantity') }}</th>
                      <th>{{ t('catalog.recall.impact.notes') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.impactType }}</td>
                      <td>{{ row.targetNumber }}</td>
                      <td>{{ row.referenceNumber || '-' }}</td>
                      <td>{{ row.quantity }}</td>
                      <td>{{ row.notes || '-' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </section>
          } @else {
            <section class="lines-panel">
              @if (canEnqueueExecution()) {
                <div class="enqueue-row">
                  <label>
                    <span>{{ t('catalog.recall.batchSize') }}</span>
                    <input pInputText type="number" min="1" [(ngModel)]="executionBatchSize" />
                  </label>
                </div>
              }
              <p-table [value]="auditEvents()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.recall.audit.eventType') }}</th>
                    <th>{{ t('catalog.recall.audit.message') }}</th>
                    <th>{{ t('catalog.recall.audit.occurredAt') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.eventType }}</td>
                    <td>{{ row.message }}</td>
                    <td>{{ row.occurredAtUtc }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          }
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .form-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); padding-top: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.full { grid-column: 1 / -1; }
    .form-grid span { font-size: 12px; color: #475569; font-weight: 600; }
    .summary-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); margin-top: 12px; }
    .summary-grid article { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 4px; }
    .summary-grid strong { font-size: 12px; color: #64748b; }
    .tab-nav { display: flex; gap: 8px; margin-top: 12px; }
    .tab-nav button { border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; padding: 8px 12px; cursor: pointer; }
    .tab-nav button.active { border-color: #2563eb; color: #2563eb; font-weight: 600; }
    .lines-panel { margin-top: 12px; }
    .lines-panel h4 { margin: 0 0 8px; font-size: 15px; }
    .hint { color: #64748b; margin: 0; }
    .enqueue-row { margin-bottom: 10px; }
    .enqueue-row label { display: grid; gap: 6px; max-width: 160px; }
    .enqueue-row span { font-size: 12px; color: #475569; font-weight: 600; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class InventoryRecallDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly executing = signal(false);
  readonly enqueuing = signal(false);
  readonly simulating = signal(false);
  readonly submitting = signal(false);
  readonly approving = signal(false);
  readonly cancelling = signal(false);
  readonly error = signal('');
  readonly campaign = signal<InventoryRecallCampaign | null>(null);
  readonly lines = signal<InventoryRecallLine[]>([]);
  readonly impactLines = signal<InventoryRecallImpactLine[]>([]);
  readonly auditEvents = signal<InventoryRecallAuditEvent[]>([]);
  readonly activeTab = signal<'targets' | 'impact' | 'audit'>('targets');
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly lots = signal<InventoryLot[]>([]);

  readonly form = {
    campaignNumber: '',
    name: '',
    reasonCode: '',
    description: '',
    offeringId: '',
    lotId: ''
  };

  executionBatchSize = 50;

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');
  readonly offeringOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.offerings().map(o => ({ label: `${o.code} - ${o.title}`, value: o.id }))
  ]);
  readonly lotOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.lots().map(l => ({ label: l.lotNumber, value: l.id }))
  ]);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows.filter(o => o.inventoryEnabled)) });
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.service.getRecallCampaign(id).subscribe({
      next: campaign => {
        this.applyCampaign(campaign);
        if (campaign.offeringId) {
          this.loadLots(campaign.offeringId);
        }
        if (campaign.simulatedAtUtc) {
          this.loadImpact(id);
        }
        this.loading.set(false);
      },
      error: () => {
        this.campaign.set(null);
        this.error.set(this.t('catalog.recall.campaigns.notFound'));
        this.loading.set(false);
      }
    });
  }

  canSimulate(): boolean {
    const status = this.campaign()?.status;
    return status === 'Draft' || status === 'Approved';
  }

  canSubmitForApproval(): boolean {
    const campaign = this.campaign();
    return campaign?.status === 'Draft' && !!campaign.simulatedAtUtc;
  }

  canApprove(): boolean {
    return this.campaign()?.status === 'PendingApproval';
  }

  canExecute(): boolean {
    const status = this.campaign()?.status;
    return status === 'Draft' || status === 'Approved' || status === 'Executing';
  }

  canEnqueueExecution(): boolean {
    const status = this.campaign()?.status;
    return status === 'Approved' || status === 'Executing';
  }

  canCancel(): boolean {
    const status = this.campaign()?.status;
    return status === 'Draft' || status === 'PendingApproval' || status === 'Approved';
  }

  executeLabel(): string {
    return this.campaign()?.status === 'Executing'
      ? this.t('catalog.recall.action.continueExecute')
      : this.t('catalog.recall.action.execute');
  }

  save(): void {
    if (!this.isCreateMode()) return;

    if (!this.form.campaignNumber.trim() || !this.form.name.trim() || !this.form.reasonCode.trim()) {
      this.error.set(this.t('catalog.recall.campaigns.requiredFields'));
      return;
    }

    if (!this.form.offeringId && !this.form.lotId) {
      this.error.set(this.t('catalog.recall.campaigns.scopeRequired'));
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.service.createRecallCampaign({
      campaignNumber: this.form.campaignNumber.trim(),
      name: this.form.name.trim(),
      reasonCode: this.form.reasonCode.trim(),
      description: this.form.description.trim(),
      offeringId: this.form.offeringId || null,
      lotId: this.form.lotId || null,
      executionKey: crypto.randomUUID().replace(/-/g, '')
    }).subscribe({
      next: created => {
        this.saving.set(false);
        void this.router.navigate(['/inventory/recalls', created.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.recall.campaigns.createFailed'));
      }
    });
  }

  simulate(): void {
    const campaign = this.campaign();
    if (!campaign) return;

    this.simulating.set(true);
    this.error.set('');
    this.service.simulateRecallCampaign(campaign.id).subscribe({
      next: result => {
        this.applyCampaign(result.campaign);
        this.impactLines.set(result.impactLines);
        this.activeTab.set('impact');
        this.simulating.set(false);
      },
      error: () => {
        this.simulating.set(false);
        this.error.set(this.t('catalog.recall.campaigns.simulateFailed'));
      }
    });
  }

  submitForApproval(): void {
    const campaign = this.campaign();
    if (!campaign) return;

    this.submitting.set(true);
    this.error.set('');
    this.service.submitRecallForApproval(campaign.id).subscribe({
      next: updated => {
        this.applyCampaign(updated);
        this.submitting.set(false);
      },
      error: () => {
        this.submitting.set(false);
        this.error.set(this.t('catalog.recall.campaigns.submitFailed'));
      }
    });
  }

  approve(): void {
    const campaign = this.campaign();
    if (!campaign) return;

    this.approving.set(true);
    this.error.set('');
    this.service.approveRecallCampaign(campaign.id).subscribe({
      next: updated => {
        this.applyCampaign(updated);
        this.approving.set(false);
      },
      error: () => {
        this.approving.set(false);
        this.error.set(this.t('catalog.recall.campaigns.approveFailed'));
      }
    });
  }

  execute(): void {
    const campaign = this.campaign();
    if (!campaign) return;

    this.executing.set(true);
    this.error.set('');
    this.service.executeRecallCampaign(campaign.id).subscribe({
      next: result => {
        this.applyCampaign(result.campaign);
        this.executing.set(false);
      },
      error: () => {
        this.executing.set(false);
        this.error.set(this.t('catalog.recall.campaigns.executeFailed'));
      }
    });
  }

  enqueueExecution(): void {
    const campaign = this.campaign();
    if (!campaign) return;

    const batchSize = Math.max(1, Number(this.executionBatchSize) || 50);
    this.enqueuing.set(true);
    this.error.set('');
    this.service.enqueueRecallExecution(
      campaign.id,
      batchSize,
      crypto.randomUUID().replace(/-/g, '')
    ).subscribe({
      next: () => {
        this.enqueuing.set(false);
        this.loadAudit(campaign.id);
        this.activeTab.set('audit');
      },
      error: () => {
        this.enqueuing.set(false);
        this.error.set(this.t('catalog.recall.enqueueFailed'));
      }
    });
  }

  openAuditTab(): void {
    this.activeTab.set('audit');
    const id = this.campaign()?.id;
    if (id) {
      this.loadAudit(id);
    }
  }

  cancel(): void {
    const campaign = this.campaign();
    if (!campaign) return;

    this.cancelling.set(true);
    this.error.set('');
    this.service.cancelRecallCampaign(campaign.id).subscribe({
      next: updated => {
        this.applyCampaign(updated);
        this.cancelling.set(false);
      },
      error: () => {
        this.cancelling.set(false);
        this.error.set(this.t('catalog.recall.campaigns.cancelFailed'));
      }
    });
  }

  onOfferingChange(offeringId: string): void {
    this.form.offeringId = offeringId;
    this.form.lotId = '';
    this.loadLots(offeringId);
  }

  private applyCampaign(campaign: InventoryRecallCampaign): void {
    this.campaign.set(campaign);
    this.lines.set(campaign.lines ?? []);
    this.form.campaignNumber = campaign.campaignNumber;
    this.form.name = campaign.name;
    this.form.reasonCode = campaign.reasonCode;
    this.form.description = campaign.description;
    this.form.offeringId = campaign.offeringId ?? '';
    this.form.lotId = campaign.lotId ?? '';
  }

  private loadImpact(id: string): void {
    this.service.getRecallImpact(id).subscribe({
      next: result => this.impactLines.set(result.impactLines),
      error: () => this.impactLines.set([])
    });
  }

  private loadAudit(id: string): void {
    this.service.getRecallAuditTrail(id).subscribe({
      next: events => this.auditEvents.set(events),
      error: () => this.auditEvents.set([])
    });
  }

  private loadLots(offeringId: string): void {
    if (!offeringId) {
      this.lots.set([]);
      return;
    }

    this.service.listInventoryLots(offeringId).subscribe({ next: rows => this.lots.set(rows) });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
